import type { AxiosRequestConfig, InternalAxiosRequestConfig } from "axios";
import axios from "axios";
import { fetchAuthSession } from "aws-amplify/auth";
import i18n from "./locales/i18n";
import { shareInFlightGets } from "./utils/shareInFlightGets";

const baseURL = import.meta.env.VITE_API_URL;

// Définir un type pour la fonction de snackbar
type ShowSnackbarFn = (message: string, type: "success" | "error" | "info" | "warning") => void;

// Variable globale pour stocker la référence à la fonction
let showSnackbar: ShowSnackbarFn | null = null;

// Fonction pour enregistrer la fonction de snackbar
export const registerSnackbar = (fn: ShowSnackbarFn): void => {
  showSnackbar = fn;
};

// Last time each google listing `reason` was surfaced, to avoid a burst of
// identical warnings when several routes report the same problem.
const warnedAt = new Map<string, number>();

export const HTTPClient = axios.create({ baseURL });

// New saas backend (campaign create/list/deliveries/cancel under /sms/*).
// Its REST API Cognito authorizer reads the idToken from the Authorization header.
// A scheme-less value (saas-api.dev.thegiftsclub.io) is not an absolute URL, so
// axios resolves it against the app origin and every call lands on the SPA
// fallback: HTML with a 200 that no .catch ever sees.
const saasBaseURL = (import.meta.env.VITE_SAAS_API_URL ?? "").replace(/^(?!https?:\/\/)/, "https://");

export const SAASClient = axios.create({ baseURL: saasBaseURL });

const attachJwt = async (config: InternalAxiosRequestConfig) => {
  try {
    const { tokens } = await fetchAuthSession();
    const token = tokens?.idToken?.toString();
    if (token) config.headers.jwt = token;
  } catch {
    // keep whatever token is already in defaults — response interceptor handles 403
  }
  return config;
};

const attachAuthorization = async (config: InternalAxiosRequestConfig) => {
  try {
    const { tokens } = await fetchAuthSession();
    const token = tokens?.idToken?.toString();
    if (token) config.headers.Authorization = token;
  } catch {
    // keep whatever token is already in defaults — response interceptor handles 403
  }
  return config;
};

HTTPClient.interceptors.request.use(attachJwt);
SAASClient.interceptors.request.use(attachAuthorization);

shareInFlightGets(HTTPClient);
shareInFlightGets(SAASClient);

HTTPClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.message === "Network Error") {
      showSnackbar?.("Erreur de réseau. Si le problème persiste, contactez le support.", "error");
      return Promise.reject(error);
    }

    // ponytail: only the auth middleware answers 403 with a plain-text body
    // ("Fail authenticating…"). Application 403s (place not owned, Google
    // PERMISSION_DENIED proxied through) answer with JSON — those are not logouts.
    if (error.response?.status === 403 && typeof error.response.data === "string") {
      try {
        showSnackbar?.("Vous êtes déconnecté. Veuillez rafraîchir la page.", "warning");
      } catch (e) {
        console.error(e);
      }
    }

    // Google stats routes answer with { reason, voiceOfMerchant } when the listing
    // itself is the problem (duplicate, unverified, suspended). Surfacing it here
    // covers every google route without touching each chart.
    // ponytail: one warning per reason per minute — the same listing problem is
    // reported by several routes, several times per page load.
    const voiceOfMerchant = error.response?.data?.voiceOfMerchant;
    const listingMessage =
      voiceOfMerchant?.message?.[i18n.language] ?? voiceOfMerchant?.message?.en ?? voiceOfMerchant?.message?.fr;
    if (listingMessage) {
      const reason = error.response?.data?.reason ?? "UNKNOWN";
      if (Date.now() - (warnedAt.get(reason) ?? 0) > 60_000) {
        warnedAt.set(reason, Date.now());
        showSnackbar?.(listingMessage, "warning");
      }
    }
    return Promise.reject(error);
  }
);

export const swrFetcher = (config: AxiosRequestConfig) =>
  HTTPClient.request(config).then((res) => res.data);
